import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import {
  loadCatalog,
  loadCatalogSchemesAll,
} from "src/business/catalog/apiCatalog";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { createRecordColumns } from "src/business/records/createRecordColumns";
import { ZSpecificationTyped } from "src/business/specification";
import {
  loadWorkflow,
  workflowCardsLoader,
} from "src/business/workflow/apiWorkflow";
import { getWorkflowDisplayName } from "src/business/workflow/getWorkflowDisplayName";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import {
  WorkflowCardsFilter,
  ZWorkflowCardSafe,
} from "src/business/workflow/ZWorkflowCard";
import { RemoteData } from "src/common/RemoteData";
import { insertColumnAfter } from "src/components/ReactiveTable/insertColumnAfter";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { PageUrl } from "src/routes/PageUrl";

export type WorkflowCardsData = {
  table: RtStore<ZWorkflowCardSafe, WorkflowCardsFilter>;
  catalog: ZCatalog;
  schemes: ZSpecificationTyped[];
  workflow: ZWorkflow;
};

export const workflowCardsPageStore = makeAutoObservable({
  async init(workflowId: string) {
    this.setData({ status: "wait" });
    try {
      if (!workflowId) throw new Error("Workflow ID is required");
      const workflow = await loadWorkflow(workflowId);
      const catalogId = workflow.catalog?.id;
      if (!catalogId) throw new Error("Workflow has no catalog");
      const [catalog, schemes] = await Promise.all([
        loadCatalog(catalogId),
        loadCatalogSchemesAll(catalogId),
      ]);
      const table = new RtStore<ZWorkflowCardSafe, WorkflowCardsFilter>({
        rowKey: "id",
        loader: workflowCardsLoader(workflowId, schemes),
        defaultRequest: { pageSize: 20 },
      });
      this.setData({
        status: "ready",
        result: { table, catalog, schemes, workflow },
      });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  data: { status: "none" } as RemoteData<WorkflowCardsData>,
  setData(newData: RemoteData<WorkflowCardsData>) {
    this.data = newData;
  },

  get columns(): RtColumn<ZWorkflowCardSafe>[] {
    const { data } = this;
    if (data.status !== "ready") return [];
    const { catalog, schemes } = data.result;
    const recordCols = createRecordColumns(
      schemes,
      catalog.displayAttribute,
      catalog.classifiers,
    );
    return insertColumnAfter<ZWorkflowCardSafe>(
      recordCols as RtColumn<ZWorkflowCardSafe>[],
      "displayId",
      {
        key: "workflowStep",
        title: "Шаг РП",
        render: (row) => getWorkflowStepDisplayName(row.workflowStep),
        minWidth: 120,
        width: 160,
      },
    );
  },

  get workflowName(): string | undefined {
    const { data } = this;
    if (data.status !== "ready") return undefined;
    return getWorkflowDisplayName(data.result.workflow);
  },

  get title(): BreadcrumbItem[] {
    return [
      { title: "Рабочие потоки и версии", page: PageUrl.workflowStreams },
      { title: this.workflowName || "..." },
    ];
  },

  dispose() {
    this.setData({ status: "none" });
  },
});
